import React from 'react';

interface ObservabilityHelpProps {
  subTab?: string;
}

/**
 * Observability Help Content - Traces and Sessions
 */
const ObservabilityHelp: React.FC<ObservabilityHelpProps> = ({ subTab }) => {
  if (subTab === 'sessions') {
    return <SessionsHelp />;
  }
  return <TracesHelp />;
};

const TracesHelp: React.FC = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Traces
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Traces provide detailed visibility into every LLM invocation in your applications. Each trace captures the full request/response cycle, including timing, tokens, and cost data.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Understanding Trace Data
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Trace ID</span>: Unique identifier for each LLM call</p>
        <p>&#8226; <span className="font-medium">Model</span>: The LLM model used (e.g., Claude, GPT-4)</p>
        <p>&#8226; <span className="font-medium">Latency</span>: Total response time in milliseconds</p>
        <p>&#8226; <span className="font-medium">Tokens</span>: Input and output token counts</p>
        <p>&#8226; <span className="font-medium">Status</span>: Success, error, or timeout</p>
        <p>&#8226; <span className="font-medium">Cost</span>: Estimated cost based on token usage</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Filtering Traces
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        Use filters to find specific traces:
      </p>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Search</span>: Filter by trace ID, prompt name, or content</p>
        <p>&#8226; <span className="font-medium">Outcome</span>: Filter by success, error, or all</p>
        <p>&#8226; <span className="font-medium">Time Range</span>: Select a date range to narrow results</p>
        <p>&#8226; <span className="font-medium">Advanced</span>: Apply complex filters on metadata</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Trace Details
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Click on any trace to view full details including the complete prompt, response, metadata, and any associated evaluations or errors.
      </p>
    </div>

  </>
);

const SessionsHelp: React.FC = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Sessions
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Sessions group related traces together, typically representing a user conversation or workflow. This view helps you understand the full context of multi-turn interactions.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Session Data
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Session ID</span>: Unique identifier for the session</p>
        <p>&#8226; <span className="font-medium">User ID</span>: Associated user or context</p>
        <p>&#8226; <span className="font-medium">Trace Count</span>: Number of LLM calls in the session</p>
        <p>&#8226; <span className="font-medium">Duration</span>: Total time span of the session</p>
        <p>&#8226; <span className="font-medium">Total Tokens</span>: Aggregate token usage</p>
        <p>&#8226; <span className="font-medium">Total Cost</span>: Combined cost of all traces</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Analyzing Sessions
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        Sessions help you:
      </p>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Track conversation flows and user journeys</p>
        <p>&#8226; Identify patterns in multi-turn interactions</p>
        <p>&#8226; Debug issues that span multiple LLM calls</p>
        <p>&#8226; Measure aggregate performance per session</p>
      </div>
    </div>

  </>
);

export default ObservabilityHelp;
